import type { CSSProperties } from 'react';

/**
 * Single source of truth for profession / trade chip colours.
 *
 * Background colours come straight from the upstream Trusstor profession
 * catalog (the D-ORCA backend already resolves the per-project override —
 * see `buildProfessionColorIndex`). When a profession has no configured colour we
 * fall back to a neutral light gray rather than inventing a colour, mirroring
 * trusstor_dashboard's empty-state chip. Because light gray needs dark text
 * to stay legible, the foreground is paired with the fill here so every chip,
 * pill and avatar across the app makes the same readable choice.
 */

/** Light-gray chip fill shown when a profession has no upstream colour. */
export const PROFESSION_FALLBACK_COLOR = 'var(--profession-fallback-color)';

/** Readable foreground for chips rendered on the light-gray fallback fill. */
const PROFESSION_FALLBACK_FG = 'var(--profession-fallback-on)';

/** White foreground used on a real (saturated) brand colour. */
const PROFESSION_ON_COLOR = 'var(--color-surface)';

/** A profession-like record carrying an optional colour (parity with trusstor). */
interface IProfessionColorSource {
  backgroundColor?: string | null;
  activeProjects?: Record<string, { backgroundColor?: string | null } | undefined>;
}

/**
 * Mirror of trusstor_dashboard's `getProfessionBackgroundColor`: prefer the
 * per-project override on `activeProjects[projectId]`, fall back to the
 * profession's base `backgroundColor`, else empty string. Most D-ORCA FE
 * callers receive an already-resolved colour string and use `professionColor`
 * instead — this exists for the cases that hold a raw profession record.
 */
export function getProfessionBackgroundColor(
  profession: IProfessionColorSource | null | undefined,
  projectId?: string,
): string {
  if (!profession) return '';
  const perProject = projectId ? profession.activeProjects?.[projectId]?.backgroundColor : undefined;
  return perProject || profession.backgroundColor || '';
}

/**
 * Resolve a raw colour to a renderable chip fill, defaulting to the neutral
 * light gray when absent. Idempotent: passing an already-resolved fallback
 * string returns it unchanged.
 */
export function professionColor(raw?: string | null): string {
  return raw && raw.length > 0 ? raw : PROFESSION_FALLBACK_COLOR;
}

/**
 * Readable foreground for a given chip fill: white on a real brand colour,
 * dark navy on the light-gray fallback so labels and initials stay legible.
 */
export function professionTextColor(fill: string): string {
  return fill === PROFESSION_FALLBACK_COLOR ? PROFESSION_FALLBACK_FG : PROFESSION_ON_COLOR;
}

/**
 * Inline `{ backgroundColor, color }` for a profession chip / pill / avatar.
 * Accepts either a raw upstream colour or an already-resolved fallback string.
 * The inline `color` overrides the white default baked into chip CSS modules.
 */
export function professionChipStyle(raw?: string | null): CSSProperties {
  const backgroundColor = professionColor(raw);
  return { backgroundColor, color: professionTextColor(backgroundColor) };
}
